/** Map offer/conversation API shapes to UI fields (shallow copies — avoids stack overflow on populated graphs). */

const MESSAGE_TYPE_MAP: Record<string, string> = {
  OFFER_CREATED: 'BID_PROPOSAL',
  OFFER_ACCEPTED: 'BID_ACCEPTED',
  OFFER_REJECTED: 'BID_REJECTED',
  OFFER_CANCELLED: 'BID_REJECTED',
  OFFER_EXPIRED: 'BID_REJECTED',
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const normalizeOffer = (offer: Record<string, unknown> | null | undefined) => {
  if (!isPlainObject(offer)) return null;
  const id = String(offer.id || offer._id || '');
  if (!id) return null;
  const amount = Number(offer.amount ?? offer.bidPrice ?? 0);
  return {
    id,
    _id: String(offer._id || id),
    amount,
    bidPrice: amount,
    status: String(offer.status || 'PENDING'),
    expiresAt: offer.expiresAt,
    convertedToCart: offer.convertedToCart ?? offer.status === 'CONVERTED',
  };
};

export const getSenderId = (msg: Record<string, unknown> | null | undefined): string | null => {
  if (!msg) return null;
  if (msg.senderId) return String(msg.senderId);
  const sender = msg.sender;
  if (!sender) return null;
  if (typeof sender === 'string') return sender;
  if (isPlainObject(sender)) {
    return String(sender.id || sender._id || '') || null;
  }
  return null;
};

export const normalizeMessage = (msg: Record<string, unknown>) => {
  const rawOffer =
    isPlainObject(msg.offer) ? msg.offer : isPlainObject(msg.bid) ? msg.bid : null;
  const offer = normalizeOffer(rawOffer);
  const type = MESSAGE_TYPE_MAP[String(msg.type)] || String(msg.type);
  const text = String(msg.text ?? msg.message ?? '');

  return {
    id: String(msg.id || msg._id || ''),
    _id: String(msg._id || msg.id || ''),
    type,
    message: text,
    text,
    createdAt: msg.createdAt,
    senderId: getSenderId(msg),
    sender: isPlainObject(msg.sender)
      ? {
          _id: String(msg.sender._id || msg.sender.id || ''),
          firstName: String(msg.sender.firstName || ''),
          lastName: String(msg.sender.lastName || ''),
          email: String(msg.sender.email || ''),
        }
      : undefined,
    offer,
    bid: offer,
    bidAmount: offer?.amount,
  };
};

export const normalizeMessages = (payload: unknown) => {
  if (!payload) return [];
  let list: unknown[] = [];
  if (Array.isArray(payload)) {
    list = payload;
  } else if (isPlainObject(payload)) {
    if (Array.isArray(payload.messages)) {
      list = payload.messages;
    } else if (
      isPlainObject(payload.data) &&
      Array.isArray(payload.data.messages)
    ) {
      list = payload.data.messages;
    }
  }
  const normalized = list
    .map((m) => normalizeMessage(m as Record<string, unknown>))
    .filter(Boolean);
  return normalized.length > 500 ? normalized.slice(-500) : normalized;
};

export const formatMessageTypeLabel = (type: string) =>
  type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
